import React from 'react';
import { Box, Tabs, Tab, Button, CircularProgress, Typography } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBagOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import BulkMachineDiscount from './BulkMachineDiscount';
import BulkProductDiscount from './BulkProductDiscount';
import BulkReplacement from './BulkReplacement';
import { createBulkDiscountForMachine, createBulkDiscountForProduct, createBulkReplacement, fetchBulkTargets } from '../../services/bulkActionsApi';
import { colors } from '../../../../themes/typography';
import { responsiveSpacing } from '../../../../themes/responsive';

const formatMachinePreview = (machines = []) => {
  if (!Array.isArray(machines) || machines.length === 0) {
    return '';
  }
  const unique = Array.from(new Set(machines));
  const preview = unique.slice(0, 3);
  const remainder = unique.length - preview.length;
  return remainder > 0 ? `${preview.join(', ')} +${remainder}` : preview.join(', ');
};

const buildSkippedDetailsText = (summary, t, excludeReasons = new Set()) => {
  if (!summary || !summary.reasons) {
    return '';
  }

  const entries = Object.entries(summary.reasons);
  if (entries.length === 0) {
    return '';
  }

  const parts = entries.map(([reasonKey, info]) => {
    const count = info?.count ?? 0;
    if (count === 0) {
      return null;
    }
    const machinesList = formatMachinePreview(info?.machines || []);
    if (excludeReasons.has(reasonKey)) {
      return null;
    }

    const params = {
      count,
      reason: t(`bulkSkipReason_${reasonKey}`, reasonKey),
      machinesPart: machinesList ? `: ${machinesList}` : ''
    };
    const templateKey = count === 1 ? 'bulkSkippedDetailSingular' : 'bulkSkippedDetailPlural';
    return t(templateKey, params);
  }).filter(Boolean);

  return parts.join(' • ');
};

const BulkActionsTab = ({
  clientId,
  availableTargets,
  availableProducts,
  defaultDates,
  availableTags,
  onTagsCommitted,
  creationStatus = 'pending',
  onSuccess,
  onError,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = React.useState('machineDiscount');
  const [submitting, setSubmitting] = React.useState(false);
  const machineRef = React.useRef(null);
  const productRef = React.useRef(null);
  const replacementRef = React.useRef(null);
  const [machineValid, setMachineValid] = React.useState(false);
  const [productValid, setProductValid] = React.useState(false);
  const [replacementValid, setReplacementValid] = React.useState(false);
  const [actionError, setActionError] = React.useState('');

  const buildMissingStockMessage = React.useCallback((response) => {
    const summary = response?.missingProductStock;
    if (summary?.machines?.length) {
      const machineList = summary.machines.join(', ');
      const extra = summary.remaining > 0
        ? ` ${t('bulkMissingConnector', 'y')} ${summary.remaining} ${summary.remaining === 1 ? t('bulkMissingMachineSingular', 'máquina más') : t('bulkMissingMachinePlural', 'máquinas más')}`
        : '';
      const base = t('bulkMissingBase', 'El producto no está disponible ahora en');
      const hint = t('bulkMissingHint', 'Cárgalo o ajusta la selección antes de guardar.');
      return `${base} ${machineList}${extra}. ${hint}`;
    }

    const skippedList = Array.isArray(response?.skipped) ? response.skipped : [];
    const fallbackMachine = skippedList.find(item => item?.reason === 'product_not_in_stock' && item?.machineAlias)?.machineAlias;
    if (fallbackMachine) {
      const base = t('bulkMissingBaseSingle', 'El producto no está disponible ahora en');
      const hint = t('bulkMissingHint', 'Cárgalo o ajusta la selección antes de guardar.');
      return `${base} ${fallbackMachine}. ${hint}`;
    }

    return null;
  }, [t]);

  const [filteredTargets, setFilteredTargets] = React.useState(availableTargets);
  const [filtering, setFiltering] = React.useState(false);
  const filterRequestId = React.useRef(0);
  const [filteredProductId, setFilteredProductId] = React.useState(null);

  React.useEffect(() => {
    if (filteredProductId) {
      handleProductFilterChange(filteredProductId);
    } else {
      setFilteredTargets(availableTargets);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTargets]);

  const machineOptions = availableTargets?.machines || [];
  const sectorOptions = filteredTargets?.sectors || [];

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    if (newValue !== 'productDiscount' && newValue !== 'replacement') {
      setFilteredTargets(availableTargets);
      setFilteredProductId(null);
    }
  };

  const handleProductFilterChange = React.useCallback(async (productId) => {
    if (!productId) {
      filterRequestId.current += 1;
      setFilteredTargets(availableTargets);
      setFilteredProductId(null);
      setFiltering(false);
      return;
    }

    try {
      setFiltering(true);
      const requestId = ++filterRequestId.current;
      setFilteredProductId(productId);
      const targets = await fetchBulkTargets(clientId, { productId });
      if (requestId === filterRequestId.current) {
        setFilteredTargets(targets);
      }
    } catch (error) {
      console.error('[BulkActionsTab] Failed to fetch filtered targets', error);
      setFilteredTargets(availableTargets);
      setFilteredProductId(null);
    } finally {
      setFiltering(false);
    }
  }, [clientId, availableTargets]);

  const withCreationStatus = React.useCallback((payload) => {
    if (!creationStatus || !payload || typeof payload !== 'object') {
      return payload;
    }
    if ('status' in payload && payload.status) {
      return payload;
    }
    return { ...payload, status: creationStatus };
  }, [creationStatus]);

  const handleSubmit = async (action, payload) => {
    console.log('[BulkActionsTab] Submitting bulk action', {
      action,
      payload,
      clientId,
    });
    setActionError('');
    try {
      setSubmitting(true);
      const requestPayload = withCreationStatus(payload);
      let response;
      if (action === 'machineDiscount') {
        console.log('[BulkActionsTab] Calling createBulkDiscountForMachine');
        response = await createBulkDiscountForMachine(clientId, requestPayload);
      } else if (action === 'productDiscount') {
        console.log('[BulkActionsTab] Calling createBulkDiscountForProduct');
        response = await createBulkDiscountForProduct(clientId, requestPayload);
      } else {
        console.log('[BulkActionsTab] Calling createBulkReplacement');
        response = await createBulkReplacement(clientId, requestPayload);
      }
      console.log('[BulkActionsTab] API response', response);
      const created = response?.createdCount ?? response?.createdExperiments?.length ?? 0;
      const isSuccess = created > 0 && response?.success !== false;
      const skippedSummary = response?.skippedSummary;
      const skipFilter = response?.message === 'bulkActionPartialActiveExperiments'
        ? new Set(['active_experiment_exists'])
        : null;
      const summaryDetails = skipFilter
        ? ''
        : buildSkippedDetailsText(skippedSummary, t);

      const baseMessage = response?.message ? t(response.message, response.messageParams || {}) : '';

      if (!isSuccess) {
        const missingMessage = buildMissingStockMessage(response);
        if (missingMessage) {
          console.warn('[BulkActionsTab] Bulk action skipped (stock)', { action, response, missingMessage });
          setActionError(missingMessage);
          return;
        }

        const resultMessage = response?.message;
        const normalizedResultMessage = typeof resultMessage === 'string'
          ? resultMessage.trim()
          : '';
        const normalizedKey = normalizedResultMessage && normalizedResultMessage.replace(/\.$/, '');
        const fallbackMessage = baseMessage
          || (normalizedKey && (normalizedKey === 'No stock snapshot found for selected machine' || normalizedKey === 'noStockSnapshotFound')
            ? t('noStockSnapshotFound')
            : (resultMessage
              ? t(resultMessage, response?.messageParams || resultMessage)
              : t('bulkActionNoneScheduled') || 'No se crearon experimentos.'));
        const combinedMessage = [fallbackMessage, summaryDetails].filter(Boolean).join('. ');
        console.warn('[BulkActionsTab] Bulk action skipped', { action, response, fallbackMessage });
        setActionError(combinedMessage || fallbackMessage);
        onError?.(combinedMessage || fallbackMessage);
        return;
      }

      if (Array.isArray(requestPayload?.tags) && requestPayload.tags.length) {
        const existingNames = new Set(
          (availableTags || []).map(tag => (tag?.name || '').toLowerCase())
        );
        const newTags = requestPayload.tags.filter(tag => tag?.name && !existingNames.has(tag.name.toLowerCase()));
        if (newTags.length) {
          onTagsCommitted?.(newTags);
        }
      }

      onSuccess?.(t('bulkActionScheduled', { count: created }));

      const combinedSuccessMessage = skipFilter
        ? baseMessage
        : [baseMessage, summaryDetails].filter(Boolean).join('. ');
      setActionError(combinedSuccessMessage);
    } catch (error) {
      const rawMessage = error?.response?.data?.message || error?.message;
      const normalizedRawMessage = typeof rawMessage === 'string'
        ? rawMessage.trim()
        : '';
      const normalizedKey = normalizedRawMessage && normalizedRawMessage.replace(/\.$/, '');
      const message = normalizedKey && (normalizedKey === 'No stock snapshot found for selected machine' || normalizedKey === 'noStockSnapshotFound')
        ? t('noStockSnapshotFound')
        : (rawMessage ? t(rawMessage, rawMessage) : t('bulkActionFailed'));
      onError?.(message);
      setActionError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const activeRef = React.useMemo(() => {
    if (activeTab === 'machineDiscount') return machineRef;
    if (activeTab === 'productDiscount') return productRef;
    return replacementRef;
  }, [activeTab]);

  const canSave = React.useMemo(() => {
    if (activeTab === 'machineDiscount') return machineValid;
    if (activeTab === 'productDiscount') return productValid;
    return replacementValid;
  }, [activeTab, machineValid, productValid, replacementValid]);

  const handleCancelClick = () => {
    if (submitting) return;
    onCancel?.();
  };

  const handleSaveClick = () => {
    if (submitting || !canSave) return;
    const ref = activeRef?.current;
    if (ref && typeof ref.submit === 'function') {
      ref.submit();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: '100%', maxWidth: 600, display: 'flex', flexDirection: 'column', gap: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 44,
              minWidth: 0,
              color: 'text.secondary',
              borderRadius: '12px',
              mx: 0.75,
            },
            '& .Mui-selected': {
              backgroundColor: 'action.hover',
              color: 'text.primary',
            },
            '& .MuiTabs-indicator': {
              height: 0,
            },
          }}
        >
          <Tab
            value="machineDiscount"
            icon={<StorefrontIcon fontSize="small" sx={{ color: 'primary.main' }} />}
            aria-label={t('machineDiscountTab')}
            title={t('machineDiscountTab')}
          />
          <Tab
            value="productDiscount"
            icon={<ShoppingBagIcon fontSize="small" sx={{ color: 'success.main' }} />}
            aria-label={t('productDiscountTab')}
            title={t('productDiscountTab')}
          />
          <Tab
            value="replacement"
            icon={<SwapHorizIcon fontSize="small" sx={{ color: 'warning.main' }} />}
            aria-label={t('replacementTab')}
            title={t('replacementTab')}
          />
        </Tabs>

        {activeTab === 'machineDiscount' && (
          <BulkMachineDiscount
            machines={machineOptions}
            onSubmit={(payload) => handleSubmit('machineDiscount', payload)}
            defaultDates={defaultDates}
            availableTags={availableTags}
            onValidityChange={setMachineValid}
            ref={machineRef}
          />
        )}

        {activeTab === 'productDiscount' && (
          <BulkProductDiscount
            sectors={sectorOptions}
            products={availableProducts}
            onSubmit={(payload) => handleSubmit('productDiscount', payload)}
            defaultDates={defaultDates}
            availableTags={availableTags}
            onProductChange={handleProductFilterChange}
            isFiltering={filtering}
            onValidityChange={setProductValid}
            ref={productRef}
          />
        )}

        {activeTab === 'replacement' && (
          <BulkReplacement
            sectors={sectorOptions}
            products={availableProducts}
            onSubmit={(payload) => handleSubmit('replacement', payload)}
            defaultDates={defaultDates}
            availableTags={availableTags}
            onProductChange={handleProductFilterChange}
            isFiltering={filtering}
            onValidityChange={setReplacementValid}
            ref={replacementRef}
          />
        )}

        {actionError && (
          <Box
            sx={{
              mt: 1.2,
              display: 'inline-flex',
              alignSelf: 'flex-start',
              backgroundColor: '#FDECEA',
              borderRadius: 4,
              px: 1.25,
              py: 0.6,
              maxWidth: '100%'
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#5C2B29', lineHeight: 1.4, fontWeight: 500, mt: 0 }}
            >
              {actionError}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: responsiveSpacing.sm,
            mt: actionError ? 1.2 : responsiveSpacing.md,
          }}
        >
          <Button
            onClick={handleCancelClick}
            disabled={submitting}
            sx={{
              color: colors.text.secondary,
              textTransform: 'none',
            }}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleSaveClick}
            disabled={!canSave || submitting}
            variant="contained"
            sx={{
              backgroundColor: colors.action.primary,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: colors.action.primaryDark || '#1976d2',
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('save') || 'Save'
            )}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default BulkActionsTab;
